import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Reservation, ReservationsExportFieldSettings,ReservationsCsvExportParams } from '@model/reservation';
import { ReservationService } from '../lib/reservation.service';
import * as ExcelJS from 'exceljs';
import { PmsInfo, PmsInfoEntry } from '@model/customer';

type ExportFieldKey = Exclude<keyof ReservationsExportFieldSettings, 'delimiter'>;

@Injectable({
  providedIn: 'root'
})
export class ReservationsExportService {

  constructor(
    private translate: TranslateService,
    private reservationService: ReservationService
  ) { }

  getDefaultFieldSettings(): ReservationsExportFieldSettings {
    return {
      status: true,
      purpose: true,
      arrivalDate: true,
      arrivalTime: true,
      fullname: true,
      covers: true,
      type: true,
      telephone: true,
      allergies: false,
      reservationTags: false,
      customerTags: false,
      discount: false,
      cake: false,
      fixMenu: false,
      smokingArea: false,
      email: false,
      category: true,
      reservationId: false,
      tables: true,
      duration: false,
      returnTime: false,
      createdBy: false,
      confirmedBy: false,
      notes: true,
      source: true,
      sourceId: false,
      customerId: false,
      bill: false,
      restaurantName: false,
      creationTime: false,
      timePerformed: false,
      metadata: false,
      delimiter: ','
    };
  }

  exportToCsv(params: ReservationsCsvExportParams): boolean {
    const reservations = params.reservations ?? [];

    if (reservations.length === 0) {
      return false;
    }

    const csv = this.buildCsv(params);
    const fileName = this.constructFileName(params, 'csv');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    this.downloadBlob(blob, fileName);
    return true;
  }

  async exportToExcel(params: ReservationsCsvExportParams): Promise<boolean> {
    const reservations = params.reservations ?? [];

    if (reservations.length === 0) {
      return false;
    }

    const selectedKeys = this.getSelectedFieldKeys(params.settings);
    const headers = selectedKeys.map(key => this.getHeaderLabel(key));
    const rows = reservations.map(reservation =>
      this.buildRawRow(reservation, params.settings, params.restaurantTitle ?? '')
    );

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Reservations');

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 20;

    headerRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF00FFFF' }
      };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    for (const values of rows) {
      const row = worksheet.addRow(values);
      row.height = 20;

      row.eachCell(cell => {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    }

    this.applyColumnWidths(worksheet, selectedKeys, headers, rows);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob(
      [buffer],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    this.downloadBlob(blob, this.constructFileName(params, 'xlsx'));
    return true;
  }

  buildCsv(params: ReservationsCsvExportParams): string {
    const delimiter = params.settings.delimiter || ',';
    const headers = this.buildHeaders(params.settings);
    const rows = (params.reservations ?? []).map(reservation =>
      this.buildRawRow(reservation, params.settings, params.restaurantTitle ?? '')
    );

    return [
      headers.map(value => this.escapeCsv(value)).join(delimiter),
      ...rows.map(row => row.map(value => this.escapeCsv(value)).join(delimiter))
    ].join('\n');
  }

  private buildHeaders(settings: ReservationsExportFieldSettings): string[] {
    return this.getSelectedFieldKeys(settings).map(key => this.getHeaderLabel(key));
  }

  private getSelectedFieldKeys(settings: ReservationsExportFieldSettings): ExportFieldKey[] {
    const orderedKeys: ExportFieldKey[] = [
      'status',
      'purpose',
      'arrivalDate',
      'arrivalTime',
      'fullname',
      'covers',
      'type',
      'telephone',
      'allergies',
      'reservationTags',
      'customerTags',
      'discount',
      'cake',
      'fixMenu',
      'smokingArea',
      'email',
      'category',
      'reservationId',
      'tables',
      'duration',
      'returnTime',
      'createdBy',
      'confirmedBy',
      'notes',
      'source',
      'sourceId',
      'customerId',
      'bill',
      'restaurantName',
      'creationTime',
      'timePerformed',
      'metadata'
    ];

    return orderedKeys.filter(key => settings[key] === true);
  }

  private getHeaderLabel(key: ExportFieldKey): string {
    switch (key) {
      case 'status': return this.t('reservationsExport.fields.status', 'Status');
      case 'purpose': return this.t('reservationsExport.fields.purpose', 'Purpose');
      case 'arrivalDate': return this.t('reservationsExport.fields.arrivalDate', 'Arrival Date');
      case 'arrivalTime': return this.t('reservationsExport.fields.arrivalTime', 'Arrival Time');
      case 'fullname': return this.t('reservationsExport.fields.fullname', 'Fullname');
      case 'covers': return this.t('reservationsExport.fields.covers', 'Covers');
      case 'type': return this.t('reservationsExport.fields.type', 'Type');
      case 'telephone': return this.t('reservationsExport.fields.telephone', 'Telephone');
      case 'allergies': return this.t('reservationsExport.fields.allergies', 'Allergies');
      case 'reservationTags': return this.t('reservationsExport.fields.reservationTags', 'Reservation Tags');
      case 'customerTags': return this.t('reservationsExport.fields.customerTags', 'Customer Tags');
      case 'discount': return this.t('reservationsExport.fields.discount', 'Discount');
      case 'cake': return this.t('reservationsExport.fields.cake', 'Cake');
      case 'fixMenu': return this.t('reservationsExport.fields.fixMenu', 'Fix Menu');
      case 'smokingArea': return this.t('reservationsExport.fields.smokingArea', 'Smoking Area');
      case 'email': return this.t('reservationsExport.fields.email', 'E-Mail');
      case 'category': return this.t('reservationsExport.fields.category', 'Category');
      case 'reservationId': return this.t('reservationsExport.fields.reservationId', 'Reservation ID');
      case 'tables': return this.t('reservationsExport.fields.tables', 'Tables');
      case 'duration': return this.t('reservationsExport.fields.duration', 'Duration');
      case 'returnTime': return this.t('reservationsExport.fields.returnTime', 'Return Time');
      case 'createdBy': return this.t('reservationsExport.fields.createdBy', 'Created By');
      case 'confirmedBy': return this.t('reservationsExport.fields.confirmedBy', 'Confirmed By');
      case 'notes': return this.t('reservationsExport.fields.notes', 'Notes');
      case 'source': return this.t('reservationsExport.fields.source', 'Source');
      case 'sourceId': return this.t('reservationsExport.fields.sourceId', 'Source ID');
      case 'customerId': return this.t('reservationsExport.fields.customerId', 'Customer ID');
      case 'bill': return this.t('reservationsExport.fields.bill', 'Bill');
      case 'restaurantName': return this.t('reservationsExport.fields.restaurantName', 'Restaurant Name');
      case 'creationTime': return this.t('reservationsExport.fields.creationTime', 'Created At');
      case 'timePerformed': return this.t('reservationsExport.fields.timePerformed', 'Modified At');
      case 'metadata': return this.t('reservationsExport.fields.metadata', 'Metadata');
      default: return key;
    }
  }

  private buildRawRow(
    reservation: Reservation,
    settings: ReservationsExportFieldSettings,
    restaurantTitle: string
  ): string[] {
    const selectedKeys = this.getSelectedFieldKeys(settings);
    return selectedKeys.map(key => this.getFieldValue(key, reservation, restaurantTitle));
  }

  private getFieldValue(
    key: ExportFieldKey,
    reservation: Reservation,
    restaurantTitle: string
  ): string {
    const arrival = this.parseDate(reservation?.reservationDetails?.arrivalTime);
    const returnTime = this.parseDate(reservation?.reservationDetails?.returnTime);

    switch (key) {
      case 'status':
        return this.valueOrEmpty(reservation?.reservationDetails?.reservationsStatus);

      case 'purpose':
        return this.valueOrEmpty(reservation?.reservationDetails?.purpose);

      case 'arrivalDate':
        return arrival ? arrival.format('DD-MM-YYYY') : '';

      case 'arrivalTime':
        return arrival ? arrival.format('HH:mm') : '';

      case 'fullname':
        return this.buildName(reservation);

      case 'covers':
        return this.toStringValue(reservation?.reservationDetails?.covers);

      case 'type':
        return this.valueOrEmpty(reservation?.reservationDetails?.reservationType);

      case 'telephone':
        return this.valueOrEmpty(reservation?.customerDetails?.telephone);

      case 'allergies':
        return this.firstNonEmpty(
          reservation?.reservationDetails?.allergy,
          (reservation?.customerDetails as any)?.allergy
        );

      case 'reservationTags':
        return this.stringifyTags((reservation?.reservationDetails as any)?.reservationTags);

      case 'customerTags':
        return this.stringifyTags((reservation?.customerDetails as any)?.customerTags);

      case 'discount':
        return this.toStringValue(reservation?.reservationDetails?.discount);

      case 'cake':
        return this.boolToYesNo(reservation?.reservationDetails?.hasCake);

      case 'fixMenu':
        return this.firstNonEmpty(
          reservation?.reservationDetails?.selectedMenu,
          reservation?.reservationDetails?.fixMenu === true ? 'true' : ''
        );

      case 'smokingArea':
        return this.boolToYesNo(reservation?.reservationDetails?.smokingArea);

      case 'email':
        return this.valueOrEmpty(reservation?.customerDetails?.mail);

      case 'category':
        return this.getCategoryDisplay(reservation);

      case 'reservationId':
        return this.valueOrEmpty(reservation?.reservationDetails?.reservationId);

      case 'tables':
        return this.valueOrEmpty(reservation?.reservationDetails?.allocatedTables);

      case 'duration':
        return this.toStringValue(reservation?.reservationDetails?.duration);

      case 'returnTime':
        return returnTime ? returnTime.format('HH:mm') : '';

      case 'createdBy':
        return this.valueOrEmpty(reservation?.reservationDetails?.createdBy);

      case 'confirmedBy':
        return this.valueOrEmpty(reservation?.reservationDetails?.confirmedBy);

      case 'notes':
        return this.valueOrEmpty(reservation?.reservationDetails?.notes);

      case 'source':
        return this.getSourceDisplay(reservation);

      case 'sourceId':
        return this.valueOrEmpty(reservation?.sourceDetails?.id);

      case 'customerId':
        return this.valueOrEmpty((reservation?.customerDetails as any)?.customerId);

      case 'bill':
        return this.toStringValue(reservation?.reservationDetails?.bill);

      case 'restaurantName':
        return this.valueOrEmpty(restaurantTitle);

      case 'creationTime':
        return this.formatTimestamp(reservation?.reservationDetails?.creationTime);

      case 'timePerformed':
        return this.formatTimestampOrString(reservation?.timePerformed);

      case 'metadata':
         return this.formatMetadataForExport(reservation?.reservationDetails?.metadata);

      default:
        return '';
    }
  }

  private constructFileName(params: ReservationsCsvExportParams, extension: 'csv' | 'xlsx'): string {
    const cleanOverride = params.fileNameOverride?.trim();

    if (cleanOverride) {
      const withoutExtension = cleanOverride.replace(/\.(csv|xlsx|xls)$/i, '');
      return `${withoutExtension}.${extension}`;
    }

    const fileNameDateAndShift = `${params.selectedShiftTitle?.trim() || 'All Day'} shift ${this.formatDateLabel(params.selectedDate)}`;
    return `Reservations list ${fileNameDateAndShift}.${extension}`;
  }

  private formatDateLabel(value?: Date | string | null): string {
    if (!value) {
      return moment().format('DD-MM-YYYY');
    }

    if (value instanceof Date) {
      return moment(value).format('DD-MM-YYYY');
    }

    const parsed = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : moment().format('DD-MM-YYYY');
  }

  private buildName(reservation: Reservation): string {
    const surname = reservation?.customerDetails?.surname?.trim() ?? '';
    const name = reservation?.customerDetails?.name?.trim() ?? '';

    if (surname && name) {
      return `${surname} ${name}`;
    }

    if (surname) {
      return surname;
    }

    if (name) {
      return name;
    }

    if (reservation?.reservationDetails?.walkIn === true) {
      return 'Walk In';
    }

    return '';
  }

  private getCategoryDisplay(reservation: Reservation): string {
    const category = reservation?.reservationDetails?.onlineCategory;

    if (!category?.id) {
      return '-';
    }

    if (category.nameLocalized?.trim()) {
      return category.nameLocalized.trim();
    }

    if (category.name?.trim()) {
      const rawName = category.name.trim();

      try {
        const parsed = JSON.parse(rawName);
        const currentLang = this.translate.currentLang || 'en';

        if (typeof parsed?.[currentLang] === 'string' && parsed[currentLang].trim()) {
          return parsed[currentLang].trim();
        }

        if (typeof parsed?.en === 'string' && parsed.en.trim()) {
          return parsed.en.trim();
        }

        const firstValue = Object.values(parsed).find(
          value => typeof value === 'string' && value.trim().length > 0
        );

        if (typeof firstValue === 'string') {
          return firstValue.trim();
        }
      } catch {
        return rawName;
      }
    }

    return category.iconCaption?.trim() || category.id;
  }

  private getSourceDisplay(reservation: Reservation): string {
    if (!reservation?.sourceDetails) {
      return '';
    }

    try {
      return this.reservationService.getSourceCaption(reservation.sourceDetails, false);
    } catch {
      return '';
    }
  }

  private stringifyTags(tags: any[] | null | undefined): string {
    if (!Array.isArray(tags) || tags.length === 0) {
      return '';
    }

    return tags
      .map(tag => tag?.title ?? tag?.name ?? '')
      .filter((x: string) => !!x?.trim())
      .join(', ');
  }

  private parseDate(value?: string | null): moment.Moment | null {
    if (!value?.trim()) {
      return null;
    }

    const parsed = moment(
      value,
      [
        'YYYY-MM-DD HH:mm:ss.S',
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601
      ],
      true
    );

    return parsed.isValid() ? parsed : null;
  }

  private formatTimestamp(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return '';
    }

    return moment(Number(value)).format('DD-MM-YYYY HH:mm:ss');
  }

  private formatTimestampOrString(value?: string | number | null): string {
    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      return this.formatTimestamp(value);
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    if (/^\d+$/.test(trimmed)) {
      return this.formatTimestamp(Number(trimmed));
    }

    const parsed = moment(
      trimmed,
      [
        'YYYY-MM-DD HH:mm:ss.S',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD HH:mm:SS.sss',
        moment.ISO_8601
      ],
      true
    );

    return parsed.isValid() ? parsed.format('DD-MM-YYYY HH:mm:ss') : trimmed;
  }

  private boolToYesNo(value?: boolean | null): string {
    return value === true ? 'Yes' : 'No';
  }

  private valueOrEmpty(value: unknown): string {
    if (value == null) {
      return '';
    }

    return String(value);
  }

  private toStringValue(value: unknown): string {
    if (value == null) {
      return '';
    }

    return String(value);
  }

  private firstNonEmpty(...values: Array<string | null | undefined>): string {
    for (const value of values) {
      if (value != null && value.toString().trim().length > 0) {
        return value.toString();
      }
    }

    return '';
  }

  private escapeCsv(value: string): string {
    const normalized = (value ?? '').replace(/\r?\n/g, ' ');
    const escaped = normalized.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  private applyColumnWidths(
    worksheet: ExcelJS.Worksheet,
    selectedKeys: ExportFieldKey[],
    headers: string[],
    rows: string[][]
  ): void {
    worksheet.columns = selectedKeys.map((key, index) => {
      const header = headers[index] ?? '';
      const values = rows.map(row => row[index] ?? '');
      const maxLength = Math.max(
        header.length,
        ...values.map(value => (value ?? '').toString().length)
      );

      let width = Math.min(Math.max(maxLength + 2, 12), 40);

      if (key === 'fullname') {
        width = Math.max(width, 25);
      }

      return { width };
    });
  }

  private parseMetadata(metadata: string | null | undefined): PmsInfo | null {
  if (!metadata?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata) as PmsInfo;
    return parsed && Array.isArray(parsed.values) ? parsed : null;
  } catch {
    return null;
  }
}

private getMetadataEntries(metadata: string | PmsInfo | null | undefined): PmsInfoEntry[] {
  if (!metadata) {
    return [];
  }

  const parsed = typeof metadata === 'string'
    ? this.parseMetadata(metadata)
    : metadata;

  return (parsed?.values ?? []).filter(entry => !!entry?.value?.trim());
}

private getMetadataCaption(entry: PmsInfoEntry): string {
  const currentLang = this.translate.currentLang || 'en';

  return currentLang === 'el'
    ? (entry.captionGreek || entry.captionEnglish || entry.key || '')
    : (entry.captionEnglish || entry.captionGreek || entry.key || '');
}

private splitMetadataValue(value?: string): string[] {
  if (!value?.trim()) {
    return [];
  }

  return value
    .split('|')
    .map(x => x.trim())
    .filter(Boolean);
}

private formatMetadataForExport(metadata: string | null | undefined): string {
  const entries = this.getMetadataEntries(metadata);

  if (entries.length === 0) {
    return '';
  }

  return entries
    .map(entry => {
      const caption = this.getMetadataCaption(entry);
      const values = this.splitMetadataValue(entry.value);

      if (values.length > 1) {
        return `${caption}: ${values.join(', ')}`;
      }

      return `${caption}: ${entry.value?.trim() ?? ''}`;
    })
    .join(' | ');
}

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);

    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.style.display = 'none';

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? fallback : value;
  }
}